import { Validators } from '@angular/forms';

import { JoinAdvancedFilterModel, TextAdvancedFilterModel } from 'ag-grid-enterprise';
import { filter, map, of } from 'rxjs';

import { InputChoices, InputTextBox } from '@camelot/form-model';
import { isArray, isNonNullable } from '@camelot/utils';

import { BaseCol, IInputOptions } from '../base-col';
import { StringEditorComponent } from './string-editor.component';
import { STRING_FILTER_MIN_LENGTH, StringFilterComponent } from './string-filter.component';

export type FilterTyping = JoinAdvancedFilterModel | TextAdvancedFilterModel;
export class StringCol extends BaseCol<FilterTyping> {
  get values() {
    return this._extractFiltersByFilterType(this.filterInstance, ['contains', 'equals'])
      .filter(cond => cond.filterType === 'text')
      .map((cond: any) => cond.filter?.toString())
      .filter(isNonNullable);
  }
  public override getInputForm(options?: IInputOptions) {
    const value = <any[]>options?.value ?? this.values;

    if (!this.data.col.multiSearchEnabled || !this.data.options.dataSearch$) {
      return new InputTextBox({
        key: this.key,
        label: options?.fromView ? '' : !options?.fromEdit ? this.inputLabel : '',
        class: 'pb-2',
        value: isArray(value) ? value[0] : [value],
        validators: options?.fromEdit ? [] : [Validators.minLength(STRING_FILTER_MIN_LENGTH)],
      });
    }

    if (this.data.col.showEntireListWhenFiltering) {
      return new InputChoices({
        key: this.key,
        label: options?.fromView ? '' : !options?.fromEdit ? this.inputLabel : '',
        value: value,
        multiple: this.data.col.multiSearchEnabled,
        showNullableFields: this.data.col.nullable,
        withSearch: true,
        onlyTemplate: options?.fromView,
        options: this.data.options.dataSearch$('', this.key).pipe(
          filter(isNonNullable),
          map(list =>
            list.map(value => ({
              id: value,
              name: value,
              data: value,
            }))
          )
        ),
      });
    }
    return new InputChoices({
      key: this.key,
      label: !options?.fromEdit ? this.inputLabel : '',
      value: value,
      multiple: this.data.col.multiSearchEnabled,
      showNullableFields: this.data.col.nullable,
      withSearch: true,
      onlyTemplate: options?.fromView,
      advancedSearch$: search => {
        if (!search || !this.data.options.dataSearch$) {
          return of([]);
        }
        return this.data.options.dataSearch$(search, this.key).pipe(
          filter(isNonNullable),
          map(list =>
            list.map(value => ({
              id: value,
              name: value,
              data: value,
            }))
          )
        );
      },
    });
  }
  public override getFiltersModel(data: any): FilterTyping | null {
    const value = data[this.key];

    if (!value || value === '' || value.length === 0) {
      return null;
    }
    if (!Array.isArray(value)) {
      if (value === '') {
        return {
          filterType: 'text',
          colId: this.key,
          type: 'blank',
        };
      }

      return {
        filterType: 'text',
        colId: this.key,
        type: this.data.col.multiSearchEnabled ? 'equals' : 'contains',
        filter: value,
      };
    }
    const filters = value.reduce<TextAdvancedFilterModel[]>((acc, val) => {
      if (val === '') {
        acc.push({
          filterType: 'text',
          colId: this.key,
          type: 'blank',
        });
      } else if (val) {
        acc.push({
          filterType: 'text',
          colId: this.key,
          type: this.data.col.multiSearchEnabled ? 'equals' : 'contains',
          filter: val,
        });
      }
      return acc;
    }, []);

    if (filters.length === 0) {
      return null;
    }
    if (filters.length === 1) {
      return filters[0];
    }
    return {
      filterType: 'join',
      type: 'OR',
      conditions: filters,
    };
  }

  public override getColDef() {
    return {
      ...super.getColDef(),
      ...{ cellDataType: 'text', cellEditor: StringEditorComponent, filter: StringFilterComponent },
    };
  }
}
